import type { AnalyticsConfig, Granularity, TimeRangeConfig } from '@/api/generated/model';
import { Granularity as GranularityEnum, TimeRangeKind } from '@/api/generated/model';
import { chartTypeMap, granularityMap } from '@/api/requests/types/charts.type';
import { AIFI_PROFICIENCY_CHART_BREAKDOWN } from '@/domains/pages/ai-proficiency/utils/aifi-aggregates-api';
import { AI_PROFICIENCY_ROLLING_WINDOW } from '@/domains/pages/ai-proficiency/utils/rolling-trend-query';
import type { TrendQueryConfig } from '@/domains/pages/shared/trend-query';
import { resolveNebulyPageTimeRange } from '@/domains/pages/shared/page-time-range';
import { getTimezoneName } from '@/utils/date.util';

export { resolveNebulyPageTimeRange as resolveAiProficiencyPageTimeRange } from '@/domains/pages/shared/page-time-range';

/** All non-chart AI proficiency aggregates always use the last 30 days. */
export const AI_PROFICIENCY_AGGREGATE_TIME_RANGE_KIND = TimeRangeKind['30_days'];

export function resolveAiProficiencyAggregateTimeRange(
  now: Date = new Date(),
  effectiveNow?: Date,
): TimeRangeConfig | undefined {
  return resolveNebulyPageTimeRange(
    {
      kind: AI_PROFICIENCY_AGGREGATE_TIME_RANGE_KIND,
      start: '',
      end: '',
      timezone: getTimezoneName(),
      recompute: true,
    },
    now,
    effectiveNow,
  );
}

/** Chart-data API supports day/week/month/year — map page granularities accordingly. */
export function mapAiProficiencyChartGranularity(granularity: Granularity): string {
  if (granularity === GranularityEnum.day) return granularityMap.day;
  if (granularity === GranularityEnum.week) return granularityMap.week;
  if (granularity === GranularityEnum.year) return granularityMap.year;
  return granularityMap.month;
}

/** Line chart config for org AI proficiency score over time (reports chart-data API). */
export function buildOrgScoreTrendChartConfig(trendQuery: TrendQueryConfig): AnalyticsConfig {
  return {
    report_type: 'analytics',
    chart_type: chartTypeMap.line,
    variables: ['aifi_score'],
    filters: [],
    special_filters: [],
    granularity: mapAiProficiencyChartGranularity(trendQuery.granularity),
    visualization_type: 'linear',
    time_range: trendQuery.time_range,
    rolling_window: AI_PROFICIENCY_ROLLING_WINDOW,
  };
}

/** Line chart config for proficiency tier level mix over time (reports chart-data API). */
export function buildLevelMixChartConfig(trendQuery: TrendQueryConfig): AnalyticsConfig {
  return {
    report_type: 'analytics',
    chart_type: chartTypeMap.line,
    variables: ['users'],
    filters: [],
    special_filters: [],
    breakdown: AIFI_PROFICIENCY_CHART_BREAKDOWN,
    granularity: mapAiProficiencyChartGranularity(trendQuery.granularity),
    visualization_type: 'linear',
    time_range: trendQuery.time_range,
    rolling_window: AI_PROFICIENCY_ROLLING_WINDOW,
  };
}
